import React, { useState, useRef, useCallback, useEffect } from "react";
import { Icon } from "@material-ui/core";
import TouchRipple from "@material-ui/core/ButtonBase";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles(() => ({
  expandIcon: {
    transition: "transform 0.25s ease",
    transform: "rotate(90deg)",
    color: "#6B7280",
  },
  collapseIcon: {
    transition: "transform 0.25s ease",
    transform: "rotate(0deg)",
    color: "#6B7280",
  },
  expansionPanel: {
    overflow: "hidden",
    transition: "max-height 0.25s ease",
  },
  submenu: {
    paddingLeft: 8,
    paddingBottom: 4,
  },
  navItem: {
    color: "#374151",
    borderRadius: 6,
    marginBottom: 4,
    transition: "all 150ms ease-in",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 44,
    width: "100%",
    "&:hover": {
      backgroundColor: "#FFF5EB",
      color: "#F26B1D",
      "& $itemIcon": {
        color: "#F26B1D",
      },
    },
  },
  navItemOpen: {
    backgroundColor: "#FFF5EB",
    color: "#F26B1D",
    "& $itemIcon": {
      color: "#F26B1D",
    },
  },
  badge: {
    color: "#F26B1D",
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginRight: 8,
  },
  compactNavItem: {
    "& $itemText": {
      display: "none",
    },
  },
  itemIcon: {
    color: "#4B5563",
    width: 36,
    minWidth: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  itemText: {
    fontSize: "0.875rem",
    fontWeight: 500,
    paddingLeft: 12,
    flex: 1,
    textAlign: "left",
  },
  parentContent: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
}));

const MatxVerticalNavExpansionPanel = ({ item, children, mode }) => {
  const [collapsed, setCollapsed] = useState(true);
  const classes = useStyles();
  const elementRef = useRef(null);
  const componentHeight = useRef(0);
  const { pathname } = useLocation();
  const { name, icon, badge } = item;

  const handleClick = () => {
    componentHeight.current = 0;
    calcaulateHeight(elementRef.current);
    setCollapsed(!collapsed);
  };

  const calcaulateHeight = useCallback((node) => {
    if (!node) return;
    if (node.name !== "child") {
      for (let child of node.children) {
        calcaulateHeight(child);
      }
    }

    if (node.name === "child") componentHeight.current += node.scrollHeight;
    else componentHeight.current += 44;
  }, []);

  useEffect(() => {
    if (!elementRef.current) return;

    calcaulateHeight(elementRef.current);

    for (let child of elementRef.current.children) {
      if (child.getAttribute("href") === pathname) {
        setCollapsed(false);
      }
    }
  }, [pathname, calcaulateHeight]);

  return (
    <div className="mb-1">
      <TouchRipple
        className={clsx(
          "has-submenu compactNavItem whitespace-pre overflow-hidden pr-2",
          classes.navItem,
          {
            [classes.navItemOpen]: !collapsed,
            [classes.compactNavItem]: mode === "compact",
          },
        )}
        onClick={handleClick}
      >
        <div className={clsx("nav-parent-content", classes.parentContent)}>
          {icon && (
            <Icon className={clsx("text-18", classes.itemIcon)}>{icon}</Icon>
          )}
          <span className={clsx("sidenavHoverShow", classes.itemText)}>
            {name}
          </span>
        </div>
        {badge && (
          <div className={clsx(classes.badge, "sidenavHoverShow")}>
            {badge.value}
          </div>
        )}
        <div
          className={clsx("item-arrow sidenavHoverShow", {
            [classes.collapseIcon]: collapsed,
            [classes.expandIcon]: !collapsed,
          })}
        >
          <Icon fontSize="small">chevron_right</Icon>
        </div>
      </TouchRipple>

      <div
        ref={elementRef}
        className={clsx(classes.expansionPanel, classes.submenu, "submenu")}
        style={
          collapsed
            ? { maxHeight: "0px" }
            : { maxHeight: componentHeight.current + "px" }
        }
      >
        {children}
      </div>
    </div>
  );
};

export default MatxVerticalNavExpansionPanel;
